import React, { useState } from 'react';
import { motion } from 'framer-motion';

const EnhancedPulseTokenCard = ({
  signal,
  formatTimeAgo,
  onClick,
  index = 0,
  isNew = false,
  chainId = "solana",
  isLocked = false,
  onLockedClick = null,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const ca = signal.ca || signal.tokenAddress;
  const displayAddress = ca ? `${ca.substring(0, 6)}...${ca.substring(ca.length - 4)}` : "Unknown";
  
  // Use DexScreener metadata (preferred) or fallback to extracted ticker
  const tokenSymbol = signal.tokenSymbol || signal.ticker || null;
  const tokenName = signal.tokenName || null;
  const tokenImage = signal.tokenImage || null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: 1,
        boxShadow: isNew ? "0 0 30px rgba(72, 120, 255, 0.5)" : "none"
      }}
      transition={{ 
        duration: 0.6, 
        delay: isNew ? 0 : index * 0.1,
        type: "spring",
        stiffness: 200
      }}
      whileHover={isLocked ? {} : { y: -8, scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={isLocked && onLockedClick ? onLockedClick : onClick}
      className={`group relative ${isLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      {/* New token badge - Marinade Style */}
      {isNew && (
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="absolute -top-2 -right-2 z-10 bg-[#151a1a] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg font-inter"
        >
          NEW
        </motion.div>
      )}
      
      {/* Card - Marinade Finance Style (Clean White Card) */}
      <div className={`relative bg-white rounded-2xl border ${isLocked ? 'border-[#d1d5db]' : 'border-[#e5e7eb]'} p-6 transition-all duration-300 ${isLocked ? '' : 'group-hover:shadow-lg group-hover:border-[#d1d5db]'}`}>
        {/* Lock overlay for locked signals */}
        {isLocked && (
          <div className="absolute inset-0 bg-white/80 rounded-2xl flex items-center justify-center z-20 backdrop-blur-sm">
            <div className="text-center">
              <svg className="w-12 h-12 text-[#9ca3af] mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-sm font-semibold text-[#6b7280]">Unlock to View</p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center flex-1 min-w-0">
            {/* Avatar/Logo */}
            <div className="h-14 w-14 rounded-xl mr-4 flex-shrink-0 shadow-lg overflow-hidden relative">
              {tokenImage ? (
                <img
                  src={tokenImage}
                  alt={tokenSymbol || tokenName || "Token"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // Hide image and show fallback
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`h-full w-full bg-gradient-to-br from-[#151a1a] to-[#2d3436] flex items-center justify-center ${tokenImage ? 'hidden absolute inset-0' : ''}`}
              >
                {tokenSymbol ? (
                  <span className="text-white font-semibold text-lg font-pp-mori">
                    {tokenSymbol.substring(0, 4).toUpperCase()}
                  </span>
                ) : (
                  <span className="text-white font-semibold text-lg font-pp-mori">
                    {displayAddress.substring(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              {tokenSymbol ? (
                <>
                  <h3 className="font-semibold text-[#151a1a] text-xl truncate font-pp-mori">
                    ${tokenSymbol}
                  </h3>
                  <p className="text-sm text-[#6b7280] truncate mt-0.5 font-inter">
                    {tokenName || displayAddress}
                  </p>
                </>
              ) : tokenName ? (
                <>
                  <h3 className="font-semibold text-[#151a1a] text-xl truncate font-pp-mori">
                    {tokenName}
                  </h3>
                  <p className="text-sm text-[#6b7280] truncate mt-0.5 font-inter">
                    {displayAddress}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-[#151a1a] text-xl truncate font-pp-mori">
                    {displayAddress}
                  </h3>
                  <p className="text-sm text-[#6b7280] truncate mt-0.5 font-inter">
                    Contract Address
                  </p>
                </>
              )}
            </div>
          </div>
          
          {signal.signal && (signal.signal === "BUY" || signal.signal === "LONG") && (
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="text-xs font-semibold py-1.5 px-3 rounded-full ml-2 flex-shrink-0 font-inter bg-[#d1fae5] text-[#065f46] border border-[#a7f3d0]"
            >
              {signal.signal}
            </motion.div>
          )}
        </div>

        {/* Contract address */}
        <div className="mb-4 p-3 bg-[#f9fafb] rounded-xl border border-[#e5e7eb]">
          <p className="text-xs text-[#6b7280] mb-1.5 font-medium font-inter">Contract Address</p>
          <p className="font-mono text-xs text-[#151a1a] break-all leading-relaxed">
            {ca || "N/A"}
          </p>
        </div>

        {/* Called At Market Cap */}
        {signal.calledAtMarketCap && (
          <div className="mb-4">
            <div className="p-3 bg-[#f9fafb] rounded-lg border border-[#e5e7eb]">
              <p className="text-xs text-[#6b7280] mb-1 font-medium font-inter">Called At</p>
              <p className="text-sm text-[#151a1a] font-semibold truncate font-inter">
                ${signal.calledAtMarketCap >= 1000000 
                  ? (signal.calledAtMarketCap / 1000000).toFixed(2) + 'M' 
                  : signal.calledAtMarketCap >= 1000 
                    ? (signal.calledAtMarketCap / 1000).toFixed(1) + 'K'
                    : signal.calledAtMarketCap.toFixed(0)
                } MC
              </p>
            </div>
          </div>
        )}

        {/* Message preview */}
        {signal.message && (
          <div className="mb-4 p-3 bg-[#f9fafb] rounded-xl border border-[#e5e7eb]">
            <p className="text-xs text-[#6b7280] mb-2 font-medium font-inter">Message</p>
            <p className="text-sm text-[#374151] line-clamp-2 leading-relaxed font-inter">
              {signal.message}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-[#e5e7eb]">
          <span className="text-xs text-[#6b7280] font-medium font-inter">
            ⏱️ {formatTimeAgo(signal.time || signal.timestamp)}
          </span>
          <span className="text-xs text-[#6b7280] font-medium font-inter">
            📢 {signal.from || signal.from_channel || "Signal"}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default EnhancedPulseTokenCard;

